'use client';

import type { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import {
  BodyDefault,
  GroupByFieldRow,
  ListPageOptionsMenu,
  ListPageOptionsMenuItem,
  type GroupByControlProps,
} from '@terra/ui-design-system';
import { Divider } from '@terra/ui-styled-system';
import { CollapseIcon, ExpandIcon } from '@terra/ui-icons';

type ListPageGroupByOptionsMenuProps<TField extends string> =
  GroupByControlProps<TField> & {
    children?: ReactNode;
    footer?: ReactNode;
  };

export function ListPageGroupByOptionsMenu<TField extends string>({
  options,
  groupByField,
  onGroupByFieldChange,
  areAllGroupsCollapsed,
  onExpandAllGroups,
  onCollapseAllGroups,
  children,
  footer,
}: ListPageGroupByOptionsMenuProps<TField>) {
  const tGroupBy = useTranslations('designSystem.groupBy');
  const hasGroupBy = options.length > 0;

  if (!hasGroupBy && !children && !footer) {
    return null;
  }

  return (
    <ListPageOptionsMenu>
      {children}
      {hasGroupBy ? (
        <GroupByFieldRow
          options={options}
          groupByField={groupByField}
          onGroupByFieldChange={onGroupByFieldChange}
        />
      ) : null}
      {hasGroupBy && groupByField ? (
        <ListPageOptionsMenuItem
          onClick={
            areAllGroupsCollapsed ? onExpandAllGroups : onCollapseAllGroups
          }
        >
          {areAllGroupsCollapsed ? (
            <ExpandIcon size={14} />
          ) : (
            <CollapseIcon size={14} />
          )}
          <BodyDefault>
            {areAllGroupsCollapsed
              ? tGroupBy('expandAll')
              : tGroupBy('collapseAll')}
          </BodyDefault>
        </ListPageOptionsMenuItem>
      ) : null}
      {footer ? (
        <>
          <Divider borderColor="border.neutral.subtle" my="1" />
          {footer}
        </>
      ) : null}
    </ListPageOptionsMenu>
  );
}
